import { getHostRouter } from '@Src/mfe.config';
import { getCompanySlug } from 'host/mfeBridge';

/**
 * The auth-bff answers HTTP 404 on the session-scoped AuthFlow and IDV endpoints when the
 * server-side session behind the browser's cookie is gone: the client hub activity session
 * (status 16003, deleted after 20 idle minutes) or the pending auth session (status 17001,
 * deleted after 30 minutes). Every other failure on those endpoints stays a 500.
 *
 * The cookie itself never expires, so the browser keeps sending the dead id. Retrying cannot
 * succeed; the user has to start a new session.
 */
export class SessionExpiredError extends Error {
    constructor(endpoint: string) {
        super(`[Auth MFE] ${endpoint}: the session has expired`);
        this.name = 'SessionExpiredError';
    }
}

/** True when an API call failed with the auth-bff's "session is gone" response (HTTP 404). */
export const isSessionGoneResponse = (error: unknown): boolean =>
    (error as { response?: { status?: number } } | null)?.response?.status === 404;

/**
 * Sends the user to the host's existing session-expired screen
 * (certifid-v2-client-hub-host `/:companySlug/session-expired`, SessionExpiredView).
 * Its "Restart session" button reloads the link the user first opened in this tab. A full
 * page load makes the host call VerifyActivitySession at start-up, and host-bff creates a
 * new activity session (and cookie) when the old one no longer exists.
 *
 * Replaces rather than pushes, so browser Back does not land on the dead auth screen.
 */
export const redirectToSessionExpired = (): void => {
    const hostRouter = getHostRouter();
    if (!hostRouter) {
        console.error('[Auth MFE] Session expired, but no host router is available to show the session-expired screen.');
        return;
    }

    const companySlug = (hostRouter.currentRoute?.value?.params?.companySlug as string | undefined)
        || getCompanySlug()
        || 'unknown';

    console.warn('[Auth MFE] Activity session expired; routing to the host session-expired screen.');
    Promise.resolve(hostRouter.replace({ name: 'session-expired', params: { companySlug } }))
        .catch((err: unknown) => console.error('[Auth MFE] Failed to route to session-expired:', err));
};
